import type { ForwardedRef, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef, useState } from 'react'
import checkedArt from './checked.gif'
import uncheckedArt from './unchecked.gif'
import style from './style.module.sass'

interface Props
	extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'children'> {
	children?: ReactNode
	checkboxOn?: 'left' | 'right'
}

export const Checkbox = forwardRef(
	(
		{
			onChange,
			checked,
			defaultChecked,
			disabled,
			children,
			className,
			checkboxOn = 'left',
			...rest
		}: Props,
		ref: ForwardedRef<HTMLInputElement>
	) => {
		const [uncontrolledChecked, setUncontrolledChecked] = useState(
			defaultChecked ?? false
		)
		const isChecked = checked ?? uncontrolledChecked

		const control = (
			<>
				<input
					{...rest}
					ref={ref}
					type="checkbox"
					checked={checked}
					defaultChecked={defaultChecked}
					disabled={disabled}
					onChange={(event) => {
						setUncontrolledChecked(event.currentTarget.checked)
						onChange?.(event)
					}}
					className={style.input}
				/>
				<img
					src={isChecked ? checkedArt : uncheckedArt}
					alt=""
					aria-hidden="true"
					draggable={false}
					// Shares the icon treatment so the monochrome pixel art inverts
					// with the active theme instead of vanishing on a light surface.
					className={`icon ${style.art}`}
				/>
			</>
		)

		return (
			<label
				data-yakiniku="checkbox"
				data-disabled={disabled ? 'true' : undefined}
				className={[
					'flex items-center gap-2',
					style.checkbox,
					disabled ? '' : 'cursor-pointer',
					className
				]
					.filter(Boolean)
					.join(' ')}
			>
				{checkboxOn === 'left' && control}
				{children}
				{checkboxOn === 'right' && control}
			</label>
		)
	}
)
